import { useParams, Link } from 'react-router-dom';
import { useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import MovieCard from '@/components/base/MovieCard';
import { movies } from '@/mocks/movies';
import { categories } from '@/mocks/categories';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();

  const categoryInfo = useMemo(() => {
    return categories.find((c) => c.slug === slug) || categories[0];
  }, [slug]);

  const filteredMovies = useMemo(() => {
    if (!slug || slug === 'all') return movies;
    return movies.filter((m) => m.categorySlug === slug);
  }, [slug]);

  const pageTitle = slug === 'all' ? '全部电影' : `${categoryInfo.name}电影`;

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-20 pb-12">
        <div className="mx-auto max-w-[1440px] px-4 md:px-6">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm mb-6" aria-label="面包屑导航">
            <Link to="/" className="text-foreground-400 hover:text-foreground-200 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line text-foreground-500" />
            <span className="text-foreground-200 font-medium">{pageTitle}</span>
          </nav>

          <div className="mb-8">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-100">{pageTitle}</h1>
            <p className="text-sm text-foreground-400 mt-2">
              {categoryInfo.description} · 共 {filteredMovies.length} 部电影
            </p>
          </div>

          {/* Category Tabs */}
          <div className="flex items-center gap-2 overflow-x-auto pb-4 mb-6 flex-wrap">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  (slug || 'all') === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-200 text-foreground-300 hover:text-foreground-100 hover:bg-background-300/50'
                }`}
              >
                <i className={`${cat.icon} text-sm`} />
                {cat.name}
                <span className="text-xs opacity-70">{cat.count}</span>
              </Link>
            ))}
          </div>

          {/* Movie Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
            {filteredMovies.map((movie) => (
              <MovieCard key={movie.id} movie={movie} variant="poster" />
            ))}
          </div>

          {filteredMovies.length === 0 && (
            <div className="text-center py-20">
              <p className="text-foreground-400 text-lg">该分类暂无电影，敬请期待！</p>
            </div>
          )}
        </div>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: `${pageTitle} - 陌陌影视`,
            description: `${categoryInfo.description}，高清免费在线观看。`,
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.momomovies.com/' },
                { '@type': 'ListItem', position: 2, name: pageTitle, item: `https://www.momomovies.com/category/${slug}` },
              ],
            },
          }),
        }}
      />
    </>
  );
}